import { computed, observable } from 'mobx';
import type { ProjectShareMappingResponseArgs } from './project_share_mapping2';
import ProjectShareMapping, { convertProjectShareMappingResponseToArgs } from './project_share_mapping2';
import type Project from './project2';

export type ProjectShareResponseArgs = {
  id: number;
  organization_id: number;
  code: string;
  name: string;
  disabled: boolean;
  editable: boolean;
  project_share_mappings?: ProjectShareMappingResponseArgs[];
};

export type ProjectShareArgs = {
  id: number;
  organizationId: number;
  code: string;
  name: string;
  disabled: boolean;
  editable: boolean;
  projectShareMappings: ProjectShareMapping[];
};

export const convertProjectShareResponseToArgs = (response: ProjectShareResponseArgs) => {
  const args: ProjectShareArgs = {
    id: response.id,
    organizationId: response.organization_id,
    code: response.code,
    name: response.name,
    disabled: response.disabled,
    editable: response.editable,
    projectShareMappings:
      response.project_share_mappings?.map(
        mapping => new ProjectShareMapping(convertProjectShareMappingResponseToArgs(mapping))
      ) || []
  };
  return args;
};

export default class ProjectShare {
  @observable id: number;
  @observable organizationId: number;
  @observable code: string;
  @observable name: string;
  @observable disabled: boolean;
  @observable editable: boolean;
  @observable projectShareMappings: ProjectShareMapping[];

  constructor(args: ProjectShareArgs) {
    this.id = args.id;
    this.organizationId = args.organizationId;
    this.code = args.code;
    this.name = args.name;
    this.disabled = args.disabled;
    this.editable = args.editable;
    this.projectShareMappings = args.projectShareMappings;
  }

  @computed get percentages(): { project: Project; percentage: number }[] {
    const shareMappings = this.projectShareMappings;
    if (shareMappings && shareMappings.length > 0) {
      const totalWeight = shareMappings.reduce((sum, shareMapping) => sum + shareMapping.weight, 0);
      const percentageList = shareMappings.map(shareMapping =>
        Math.floor((100 * shareMapping.weight) / totalWeight)
      );
      const totalPercentage = percentageList.reduce((sum, percentage) => sum + percentage, 0);
      percentageList[0] += 100 - totalPercentage;

      return shareMappings
        .map((shareMapping, i) => ({
          project: shareMapping.project,
          percentage: percentageList[i]
        }))
        .filter((item): item is { project: Project; percentage: number } => item.project !== undefined);
    }
    return [];
  }

  @computed get shareLabel(): string {
    let content = '';
    this.percentages.forEach(item => {
      const project = item.project;
      if (project) {
        if (content) {
          content += ' : ';
        }
        content += `${project.code}-${project.name}(${item.percentage}%)`;
      }
    });

    return content;
  }

  @computed get codeAndNameOneLine(): string {
    return `${this.code}:${this.name}`;
  }

  @computed get codeAndNameLines(): string[] {
    return [`${this.code}-${this.name}`, this.shareLabel];
  }
}
